import { EMAIL_SUBSCRIBE, EMAIL_UNSUBSCRIBE } from 'constants/esDataTypes';
import noop from 'lodash/noop';
import { getQueryStub } from 'modules/workflowNodeEditors/components/startNode/utils';
import { WORKFLOW_TRIGGERS } from 'modules/workflows/constants/base';
import {
  fetchTopLevelCustomEventsRequest,
  getTopLevelCustomEvents,
  getIsSendEmailTriggerEnabled,
} from 'modules/workflows/ducks';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Add16 } from 'ui/icons/action/Add16';
import { Nest } from 'ui/icons/action/Nest';
import { SecondaryButton, BUTTON_SIZES } from 'ui/button';
import { BasicSelect } from 'ui/selects/BasicSelect';
import { body2, colors, fontWeights, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { CAMPAIGN_LABEL } from 'utils/fieldTypes';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';
import {
  FilterInputsList,
  FilterSegmentSelectInputOption,
  NestComponentProps,
  Query,
} from '../../FilterInputsList';
import { FilterSegmentSelectInput } from '../../FilterInputsList/FilterInput';
import {
  BACKEND_EVENT_NAME,
  RULE_TYPES,
  SYSTEM_EVENTS,
  SYSTEM_EVENTS_METADATA,
} from '../constants';
import { hasValidationError } from '../hooks/useRuleBuilder.utils';
import { EventAndSearchQueryValidationErrors } from '../types';
import { combinatorOptions, combinatorToOptionsIndex, getCombinatorCopy } from './RuleBlock.utils';

type EventOccursRuleProps = {
  eventName: string;
  onSearchQueriesUpdate?: (newQueries: Partial<Query>[]) => void;
  onEventSelect?: (selectedOptions: FilterSegmentSelectInputOption[]) => void;
  initialQueries: Query[];
  queries: Partial<Query>[];
  combinator: string;
  setCombinator?: (combinator: string) => void;
  errors?: EventAndSearchQueryValidationErrors;
  isDisabled?: boolean;
  isMaxOneRule?: boolean;
  isJourneyConversionMetRule?: boolean;
};

const StyledDescriptor = styled.div`
  width: ${pxToRem(124)};
  margin-right: ${spacing.base};
  display: flex;
  justify-content: flex-end;
`;

const StyledHeader = styled.div`
  ${body2}
  display: flex;
  align-items: center;
  gap: ${spacing.half};
`;

const StyledSelectContainer = styled.div`
  margin: 0;
`;

const StyledAddConditionButton = styled(SecondaryButton)`
  margin-left: auto;
`;

const StyledSingleRuleButtonContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${spacing.threeQuarter};
  gap: ${spacing.half};
`;
const StyledSingleRuleHeader = styled.div`
  ${body2};
  width: ${pxToRem(124)};
  margin-right: ${spacing.base};
  align-items: center;
`;

const StyledAdd = styled(Add16)`
  margin-right: ${spacing.quarter};
`;

export const StyledNest = styled(Nest)`
  color: ${colors.textSecondary};
  margin-right: ${spacing.half};
`;

export const combineSystemWithCustomEvents = (
  customEvents: string[],
  isSendEmailTriggerEnabled: boolean,
  isWhatsAppTriggerEnabled: boolean = false,
) => {
  const customEventOptions = customEvents.map((eventName) => ({
    field: eventName,
    fieldType: 'string',
    id: eventName,
    label: eventName,
    labelInMenu: eventName,
    labelInTriggerButton: eventName,
    value: eventName,
    ruleType: RULE_TYPES.CUSTOM_EVENT,
    dataType: 'customEvent',
  }));
  const systemEvents = SYSTEM_EVENTS.map((eventName) => ({
    field: BACKEND_EVENT_NAME[eventName],
    fieldType: 'string',
    id: BACKEND_EVENT_NAME[eventName],
    label: SYSTEM_EVENTS_METADATA[eventName]?.label,
    labelInMenu: SYSTEM_EVENTS_METADATA[eventName]?.label,
    labelInTriggerButton: SYSTEM_EVENTS_METADATA[eventName]?.label,
    value: BACKEND_EVENT_NAME[eventName],
    ruleType: RULE_TYPES.SYSTEM_EVENT,
    dataType: SYSTEM_EVENTS_METADATA[eventName].dataType,
  }));

  const allEvents = [...customEventOptions, ...systemEvents];

  const filteredEventList = [
    ...(isSendEmailTriggerEnabled ? [] : [BACKEND_EVENT_NAME[WORKFLOW_TRIGGERS.SEND_AN_EMAIL]]),
    ...(isWhatsAppTriggerEnabled
      ? []
      : [
          BACKEND_EVENT_NAME[WORKFLOW_TRIGGERS.CLICKED_A_WHATSAPP],
          BACKEND_EVENT_NAME[WORKFLOW_TRIGGERS.RECEIVED_A_WHATSAPP],
          BACKEND_EVENT_NAME[WORKFLOW_TRIGGERS.SEEN_WHATS_APP],
        ]),
  ];
  const filteredEvents =
    filteredEventList.length === 0
      ? allEvents
      : allEvents.filter((e) => !filteredEventList.includes(e.id));

  return filteredEvents.sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));
};

const StyledWhereComponent = styled.div`
  ${body2};
  color: ${colors.textSecondary};
  margin-right: ${spacing.base};
  width: ${pxToRem(120)};
  display: flex;
  justify-content: flex-end;
`;

const StyledReadOnlyCombinator = styled.div`
  font-weight: ${fontWeights.bold};
`;

export const EventOccursRule = ({
  eventName,
  onSearchQueriesUpdate = noop,
  onEventSelect = noop,
  initialQueries,
  queries,
  combinator,
  setCombinator = noop,
  errors = {},
  isDisabled = false,
  isMaxOneRule = false,
  isJourneyConversionMetRule = false,
}: EventOccursRuleProps) => {
  const dispatch = useDispatch();
  const events = useSelector(getTopLevelCustomEvents);
  const isSendEmailTriggerEnabled = useSelector(getIsSendEmailTriggerEnabled);
  const { isWhatsAppEnabled: isWhatsAppTriggerEnabled } = useWhatsAppMessageChannelFeature();

  useEffect(() => {
    if (!events.length) {
      dispatch(fetchTopLevelCustomEventsRequest());
    }
  }, []);

  const allEvents = useMemo(() => {
    return combineSystemWithCustomEvents(
      events,
      isSendEmailTriggerEnabled,
      isWhatsAppTriggerEnabled,
    );
  }, [events, isSendEmailTriggerEnabled, isWhatsAppTriggerEnabled]);

  function handleSetCombinator(combinatorSelections: string[]) {
    setCombinator(combinatorSelections[0]);
  }
  const combinatorDescriptor = getCombinatorCopy(combinator);
  const WhereComponent = useCallback(
    ({ idx }: NestComponentProps) => {
      return idx === 0 ? (
        <StyledWhereComponent>where</StyledWhereComponent>
      ) : (
        <StyledWhereComponent>{combinatorDescriptor} where</StyledWhereComponent>
      );
    },
    [combinator],
  );
  const dataType = allEvents.find((e) => e.field === eventName)?.dataType || 'customEvent';
  const isSubOrUnSub = dataType === EMAIL_SUBSCRIBE || dataType === EMAIL_UNSUBSCRIBE;
  const placeholderValue = errors?.eventName?.displayValue || 'Select an event';
  const nameDescription = isJourneyConversionMetRule ? 'Conversion met' : 'Event occurs';
  return (
    <div data-test="ruleblock-event-occurs-rule">
      <StyledHeader>
        <StyledDescriptor>{nameDescription}</StyledDescriptor>
        <FilterSegmentSelectInput
          options={allEvents}
          id="event-occurs"
          selectedOptionIds={[eventName]}
          onSelect={onEventSelect}
          placeholder={placeholderValue}
          hasError={hasValidationError(errors?.eventName)}
          isDisabled={isDisabled}
          isFilterable
        />
        {queries.length > 0 && (
          <>
            with
            <StyledSelectContainer>
              {!isDisabled ? (
                <BasicSelect
                  onSelect={handleSetCombinator}
                  id="event-combinator"
                  options={combinatorOptions}
                  defaultValues={[combinator || combinatorOptions[0].value]}
                />
              ) : (
                <StyledReadOnlyCombinator>
                  {combinatorOptions[combinatorToOptionsIndex(combinator)].text ||
                    combinatorOptions[0].text}
                </StyledReadOnlyCombinator>
              )}
            </StyledSelectContainer>
            of the following...
          </>
        )}
        {!isMaxOneRule && !isDisabled && (
          <StyledAddConditionButton
            size={BUTTON_SIZES.SMALL}
            data-test="filter-list-add-filter"
            onClick={() => onSearchQueriesUpdate([...queries, getQueryStub('customEvent')])}
          >
            <StyledAdd /> Condition
          </StyledAddConditionButton>
        )}
      </StyledHeader>
      {queries.length > 0 && (
        <FilterInputsList
          initialQueries={initialQueries}
          dataType={dataType}
          trigger=""
          queries={queries}
          onSearchQueriesUpdate={onSearchQueriesUpdate}
          canDelete={!(isSubOrUnSub && queries.length < 2)} // for now, Subscribe and Unsubscribe MUST have a value here and can't be deleted. Refactor if other events need this
          NestComponent={WhereComponent}
          canAddFilter={false}
          queriesErrors={errors?.searchQuery?.searchQueries}
          propertyMapObjectFilterString={dataType === 'customEvent' ? eventName : undefined}
          isDisabled={isDisabled}
          propertyMapDisallowedIds={[CAMPAIGN_LABEL]}
        />
      )}
      {isMaxOneRule && eventName && !isDisabled && (
        <StyledSingleRuleButtonContainer>
          <StyledSingleRuleHeader>
            <StyledWhereComponent>
              <StyledNest />
              {queries.length > 0 ? combinatorDescriptor : ''}
            </StyledWhereComponent>
          </StyledSingleRuleHeader>
          <SecondaryButton
            size={BUTTON_SIZES.SMALL}
            data-test="filter-list-add-filter"
            onClick={() => onSearchQueriesUpdate([...queries, getQueryStub('customEvent')])}
          >
            <StyledAdd /> Condition
          </SecondaryButton>
        </StyledSingleRuleButtonContainer>
      )}
    </div>
  );
};
